import React, { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllHistoryQuery } from "Services/HistoryApiSlice";
import { encodeParam } from "Utilities/urlEncoder";
import type { AdminHistory } from "Types/Admin/AdminHistoryType";
import AOS from "aos";
import "aos/dist/aos.css";

/* ---------- STYLE HELPERS ---------- */

const getContainerStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "grid",
  gridTemplateColumns: isMobile
    ? "1fr"
    : "minmax(0, 300px) minmax(0, 900px)",
  gap: isMobile ? 24 : 48,
  padding: isMobile ? "16px" : "32px 24px",
  justifyContent: "center",
});

const itemLeftStyle: React.CSSProperties = {
  padding: "18px 12px",
};

const getItemImageStyle = (isMobile: boolean): React.CSSProperties => ({
  width: "100%",
  height: isMobile ? "auto" : 340,
  maxHeight: isMobile ? 260 : undefined,
  aspectRatio: isMobile ? "16 / 9" : undefined,
  objectFit: "cover",
  borderRadius: 12,
  boxShadow: "0 4px 18px rgba(0,0,0,0.15)",
});

const getYearOverlayStyle = (isMobile: boolean): React.CSSProperties => ({
  position: "absolute",
  right: isMobile ? 12 : 20,
  top: isMobile ? 12 : 18,
  fontSize: isMobile ? 18 : 64,
  padding: isMobile ? "4px 10px" : undefined,
  background: isMobile ? "rgba(0,0,0,0.6)" : "transparent",
  borderRadius: isMobile ? 999 : 0,
  color: "#fff",
  fontWeight: 800,
  textShadow: "0 6px 20px rgba(0,0,0,0.35)",
});

/* ---------- CARD ---------- */

const TimelineCard: React.FC<{
  story: AdminHistory;
  onOpen: (id: number) => void;
  isMobile: boolean;
}> = ({ story, onOpen, isMobile }) => {
  return (
    <div
      data-aos={isMobile ? "fade-up" : "fade-right"}
      data-aos-duration="900"
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 16 : 32,
        alignItems: "center",
        marginBottom: isMobile ? 32 : 64,
      }}
    >
      {/* LEFT CONTENT */}
      <div style={{ flex: 1 }}>
        <div style={itemLeftStyle}>
          <div style={{ fontWeight: 900, fontSize: 22 }}>{story.year}</div>

          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              marginTop: 8,
              lineHeight: 1.3,
            }}
          >
            {story.title}
          </div>

          <div
            style={{
              color: "#555",
              marginTop: 10,
              whiteSpace: "pre-line",
              fontSize: 15,
            }}
          >
            {story.description1
              ? story.description1.length > 130
                ? story.description1.slice(0, 130) + "…"
                : story.description1
              : ""}
          </div>

          <div style={{ marginTop: 14 }}>
           <button
  onClick={() => onOpen(story.id)}
  className="
    inline-flex items-center gap-2
    px-5 py-2
    rounded-full
    bg-black hover:bg-green-900
    text-white
    text-sm font-medium
    transition
  "
>
  View Full Story

  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M9 5l7 7-7 7" />
  </svg>
</button>

          </div>
        </div>
      </div>

      {/* IMAGE */}
      <div
        style={{
          flex: 2,
          position: "relative",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {story.imageUrl ? (
  <img
    src={story.imageUrl}
    alt={story.title}
    style={getItemImageStyle(isMobile)}
  />
) : (
  <div
    style={{
      ...getItemImageStyle(isMobile),
      background: "#f3f3f3",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#777",
    }}
  >
    
    <div style={{ fontSize: 14, fontWeight: 500 }}>
      Image Not Available
    </div>
  </div>
)}
        <div style={getYearOverlayStyle(isMobile)}>{story.year}</div>
      </div>
    </div>
  );
};

/* ---------- PAGE ---------- */

const HistoryTimeline: React.FC = () => {
  const navigate = useNavigate();
  const plant = process.env.REACT_APP_PLANT ?? "";
  const { data: historyItems = [], isLoading } =
    useGetAllHistoryQuery({ plant });

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    AOS.init({ once: true, duration: 800, easing: "ease-out-cubic" });
  }, []);

  const sorted = useMemo(() => {
    return [...historyItems].sort(
      (a: AdminHistory, b: AdminHistory) => a.year - b.year
    );
  }, [historyItems]);

  const openStory = (id: number) => navigate(`/history-detail?hid=${encodeParam(String(id))}`);
  const goBack = () => navigate("/history");

  return (
    <main style={{ background: "#fff", minHeight: "100vh" }}>
      <div
        style={{
          padding: isMobile ? "16px" : "24px 56px",
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        {/* HEADER */}
        <div
          data-aos="fade-down"
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
            gap: isMobile ? 12 : 0,
            marginBottom: 40,
          }}
        >
          <h2
            style={{
              fontWeight: 700,
              fontSize: isMobile ? 26 : 34,
              letterSpacing: 0.5,
              margin: 0,
            }}
          >
            Browse Our Timeline
          </h2>

          <button
            onClick={goBack}
           className="
    inline-flex items-center gap-2
    px-5 py-2
    mb-8
    bg-black hover:bg-green-900
    text-white text-sm font-medium
    rounded-full
    transition
  "
  
>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M15 19l-7-7 7-7" />
  </svg>
            Back
          </button>
        </div>

        {/* CONTENT */}
        {isLoading ? (
          <div data-aos="fade" style={{ textAlign: "center", padding: 40 }}>
            Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="min-h-[50vh] flex items-center justify-center">
  <div className="text-center max-w-md px-6">
    <div className="mx-auto mb-6 w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center text-3xl">
      🕰️
    </div>

    <h3 className="text-2xl font-semibold text-gray-800">
      History Coming Soon
    </h3>

    <p className="text-gray-500 mt-3">
      Our timeline stories are being curated.
      Please visit again shortly.
    </p>

    
  </div>
</div>
        ) : (
          <div style={getContainerStyle(isMobile)}>
            {!isMobile && <div />}
            <div>
              {sorted.map((story) => (
                <TimelineCard
                  key={story.id}
                  story={story}
                  onOpen={openStory}
                  isMobile={isMobile}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default HistoryTimeline;
